import axios from "axios";
import { handlePrintPDF } from "../../utils/pdfUtils";
import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { baseurl, baseurImage } from "../../Baseurl";
import Swal from "sweetalert2";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import logo1 from "../../assests/logo.png";
import BarcodeGenerator from "../BarcodeGenerator/BarcodeGenerator";
import { fetchModuleMappings, hasSubModulePermission, isAdmin } from "../../utils/permissions";
import axiosInstance from "../../api/axiosInstance";
import Vendors from "./Vendors/Vendors";
import Batches from "./Batches/Batches";
import PurchaseOrders from "./PurchaseOrders/PurchaseOrders";
import StockMovements from "./StockMovements/StockMovements";
import Analytics from "./Analytics/Analytics";

// ─── Theme ───────────────────────────────────────────────────────────────────

const COLORS = {
  gray:   '#5B6B79',
  blue:   '#96C5F7',
  red:    '#9E2B25',
  orange: '#F29E4C',
  green:  '#2CA87F',
}

const COLORS2 = { purple: '#7c3aed', teal: '#0891b2' }

const TABS = [
  { id: 'tab1', label: 'Prescriptions',    icon: 'ti-file-description', color: COLORS.blue,    permKey: 'prescriptions'   },
  { id: 'tab2', label: 'Inventory',         icon: 'ti-package',          color: COLORS.green,   permKey: 'inventory'       },
  { id: 'tab3', label: 'Rx Standard List',  icon: 'ti-list-details',     color: COLORS.orange,  permKey: 'rxStandardList'  },
  { id: 'tab4', label: 'Vendors',           icon: 'ti-building-store',   color: COLORS2.purple, permKey: 'vendors',        advancedInventory: true },
  { id: 'tab5', label: 'Batches',           icon: 'ti-layers-subtract',  color: COLORS2.teal,   permKey: 'batches',        advancedInventory: true },
  { id: 'tab6', label: 'Purchase Orders',   icon: 'ti-shopping-cart',    color: COLORS.orange,  permKey: 'purchaseOrders', advancedInventory: true },
  { id: 'tab7', label: 'Stock Movements',   icon: 'ti-transfer-in',      color: COLORS.gray,    permKey: 'stockMovements', advancedInventory: true },
  { id: 'tab8', label: 'Analytics',         icon: 'ti-chart-bar',        color: COLORS2.purple, permKey: 'analytics',      advancedInventory: true },
]

const thStyle = {
  fontSize: 11, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase',
  letterSpacing: '0.05em', padding: '8px 10px', background: '#f8f9fb',
  borderBottom: '1px solid #e8ecf0', whiteSpace: 'nowrap',
}
const tdStyle = {
  fontSize: 13, color: '#2d3748', padding: '7px 10px',
  verticalAlign: 'middle', borderBottom: '1px solid #f0f2f5',
}
const truncateCell = {
  maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
}

const paginBtn = (disabled) => ({
  width: 28, height: 28, borderRadius: 6, border: `1px solid ${COLORS.blue}`,
  background: disabled ? '#f5f7fa' : COLORS.blue, color: disabled ? '#aaa' : '#fff',
  cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center',
  justifyContent: 'center', fontSize: 13,
})

const actionIcon = (color) => ({
  width: 30, height: 30, borderRadius: 8, border: 'none', cursor: 'pointer',
  background: `${color}22`, color, display: 'inline-flex',
  alignItems: 'center', justifyContent: 'center', fontSize: 15,
  textDecoration: 'none', marginRight: 4, flexShrink: 0,
})

// ─── Sub-components ───────────────────────────────────────────────────────────

function SearchBox({ value, onChange, placeholder }) {
  return (
    <div style={{ position: 'relative', maxWidth: 340 }}>
      <i className="ti ti-search" style={{
        position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
        color: COLORS.gray, fontSize: 14, pointerEvents: 'none',
      }} />
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          paddingLeft: 32, paddingRight: 10, height: 34, fontSize: 13,
          border: '1px solid #dde3eb', borderRadius: 8, width: '100%',
          outline: 'none', color: COLORS.gray, boxSizing: 'border-box',
        }}
      />
    </div>
  )
}

function Pagination({ currentPage, totalPages, setCurrentPage }) {
  if (!totalPages || totalPages <= 1) return null
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <button
        onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
        disabled={currentPage === 1}
        style={paginBtn(currentPage === 1)}
      >
        <i className="ti ti-chevron-left" />
      </button>
      <span style={{ fontSize: 12, color: COLORS.gray, fontWeight: 600 }}>
        {currentPage} / {totalPages}
      </span>
      <button
        onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
        disabled={currentPage === totalPages}
        style={paginBtn(currentPage === totalPages)}
      >
        <i className="ti ti-chevron-right" />
      </button>
    </div>
  )
}

function EmptyRow({ colSpan, message = 'No records found' }) {
  return (
    <tr>
      <td colSpan={colSpan} style={{ ...tdStyle, textAlign: 'center', color: COLORS.gray, padding: '36px 0' }}>
        <i className="ti ti-database-off" style={{ fontSize: 28, display: 'block', marginBottom: 6, opacity: 0.4 }} />
        {message}
      </td>
    </tr>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Pharmacy() {
  const [activeTab, setActiveTab] = useState(null);
  const location = useLocation();
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [data, setData] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [drugs, setDrugs] = useState([]);
  const [rxList, setRxList] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [searchRx, setSearchRx] = useState("");
  const [searchDrug, setSearchDrug] = useState("");
  const [searchPrescription, setSearchPrescription] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [editId, setEditId] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categoryList, setCategoryList] = useState([]);
  const [categoryData, setCategoryData] = useState({});
  const [categories, setCategories] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const pdfRef = useRef();

  const [alerts, setAlerts] = useState({ low_stock: [], near_expiry: [], expired: [] });
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [inventoryBranchFilter, setInventoryBranchFilter] = useState("all");
  const allBranchSettings = useSelector((state) => state.business.allBranchSettings);

  const [permissions, setPermissions] = useState({
    prescriptions: false,
    inventory: false,
    rxStandardList: false,
    vendors: false,
    batches: false,
    purchaseOrders: false,
    stockMovements: false,
    analytics: false,
  });
  const [advancedInventoryEnabled, setAdvancedInventoryEnabled] = useState(false);

  useEffect(() => {
    const checkPermissions = async () => {
      if (isAdmin()) {
        setPermissions({ prescriptions: true, inventory: true, rxStandardList: true, vendors: true, batches: true, purchaseOrders: true, stockMovements: true, analytics: true });
        return;
      }
      try {
        const allModules = await fetchModuleMappings();
        const pharmacyModule = allModules.find(m => m.module_name.toLowerCase() === 'pharmacy');
        if (pharmacyModule && pharmacyModule.sub_modules) {
          const prescriptionsSub  = pharmacyModule.sub_modules.find(sm => sm.sub_module_name === 'prescriptions');
          const inventorySub      = pharmacyModule.sub_modules.find(sm => sm.sub_module_name === 'inventory');
          const rxStandardListSub = pharmacyModule.sub_modules.find(sm => sm.sub_module_name === 'rx_standard_list');
          setPermissions({
            prescriptions:  prescriptionsSub  ? hasSubModulePermission(prescriptionsSub.id)  : false,
            inventory:      inventorySub      ? hasSubModulePermission(inventorySub.id)       : false,
            rxStandardList: rxStandardListSub ? hasSubModulePermission(rxStandardListSub.id)  : false,
            vendors: true, batches: true, purchaseOrders: true, stockMovements: true, analytics: true,
          });
        }
      } catch (err) {
        console.error("Error checking permissions", err);
      }
    };

    const checkAdvancedInventory = async () => {
      try {
        const res = await axiosInstance.get('getModuleSettings');
        if (res.data?.success) {
          const row = res.data.data.find(r => r.module_name === 'advanced_inventory');
          setAdvancedInventoryEnabled(Boolean(row?.enable));
        }
      } catch (err) {
        console.error("Error fetching advanced_inventory setting", err);
      }
    };

    checkPermissions();
    checkAdvancedInventory();
  }, []);

  useEffect(() => {
    if (activeTab === null) {
      const storedTab = location.state?.activeTab || sessionStorage.getItem("pharmacyActiveTab");
      let tabToSet = null;
      if (storedTab) {
        if (storedTab === "tab1" && permissions.prescriptions)  tabToSet = "tab1";
        else if (storedTab === "tab2" && permissions.inventory)  tabToSet = "tab2";
        else if (storedTab === "tab3" && permissions.rxStandardList) tabToSet = "tab3";
      }
      if (!tabToSet) {
        if (permissions.prescriptions)  tabToSet = "tab1";
        else if (permissions.inventory)  tabToSet = "tab2";
        else if (permissions.rxStandardList) tabToSet = "tab3";
      }
      if (tabToSet) setActiveTab(tabToSet);
    }
  }, [permissions, activeTab, location.state]);

  useEffect(() => {
    if (activeTab) sessionStorage.setItem("pharmacyActiveTab", activeTab);
  }, [activeTab]);

  const navigate = useNavigate();
  const itemsPerPage = 10;

  const handlePrintRow = (item) => {
    let groupMedicines = [];
    if (item.prescription_group_id) {
      groupMedicines = prescriptions.filter(p => p.prescription_group_id === item.prescription_group_id);
    } else {
      groupMedicines = [item];
    }

    const businessSetting = JSON.parse(localStorage.getItem("businessSetting"));
    const businessAddress = businessSetting?.address || "N/A";
    const businessCR      = businessSetting?.cr_number || "N/A";
    const businessVAT     = businessSetting?.vat_number || "";
    const phone           = businessSetting?.telephone_1 || "N/A";
    const hospitalName    = businessSetting?.official_name || "N/A";
    const email           = businessSetting?.email || "N/A";
    const website         = businessSetting?.website;
    const fileName        = `Prescription_${item.fullName || "Patient"}`;
    const defaultLogo     = new URL(logo1, window.location.origin).href;

    const content = `
      <div style="background: #fff;">
        <div style="text-align: center; margin-bottom: 20px;">
            <div style="text-align: center; font-weight: bold;">
                <img src="${businessSetting?.image ? `${baseurImage}${businessSetting.image}` : defaultLogo}"
                    onerror="this.onerror=null;this.src='${defaultLogo}';" alt="Logo" style="height: 80px;" />
            </div>
            <h2 style="margin-top: 30px; font-weight: 700; margin-bottom: 0px;">${hospitalName}</h2>
            <p style="margin: 0;"><strong>Address : </strong> ${businessAddress}</p>
            <p style="margin: 0;"><strong>CR:</strong> ${businessCR}</p>
            <p style="margin: 0;"><strong>VAT No.</strong> ${businessVAT}</p>
        </div>
        <div style="display: flex; justify-content: space-between; margin-top: 20px; border-top: 1px solid #000;">
            <div style="margin-top: 50px;">
                <p style="margin: 0;"><strong>Patient:</strong> ${item.fullName || item.patientName || "N/A"}</p>
                <p style="margin: 0;"><strong>Gender:</strong> ${item.gender || "N/A"}</p>
                <p style="margin: 0;"><strong>Age:</strong> ${item.age || "N/A"}</p>
                <p style="margin: 0;"><strong>CPR ID:</strong> ${item.civilIdNumber || "N/A"}</p>
                <p style="margin: 0;"><strong>Phone:</strong> ${item.mobileNumber || "N/A"}</p>
            </div>
            <div style="margin-top: 50px;">
                <p style="margin: 0;"><strong>Doctor:</strong> ${item.doctorName || "N/A"}</p>
                <p style="margin: 0;"><strong>License:</strong> ${item.doctorLicenseId || "N/A"}</p>
                <p style="margin: 0;"><strong>Date:</strong> ${item.created_at ? new Date(item.created_at).toLocaleDateString("en-GB") : new Date().toLocaleDateString("en-GB")}</p>
            </div>
        </div>
        <h1 style="border-top: 1px solid #000; padding-top: 30px; font-size: 50px;">Rx</h1>
        <div class="table-responsive">
            <table style="width: 100%; border-collapse: collapse; margin-top: 80px;">
                <thead>
                    <tr>
                        <th style="border: 1px solid #000; padding: 3px 10px; text-align: left;">SR. NO.</th>
                        <th style="border: 1px solid #000; padding: 3px 10px; text-align: left;">Medicine Name</th>
                        <th style="border: 1px solid #000; padding: 3px 10px; text-align: left;">Substance</th>
                        <th style="border: 1px solid #000; padding: 3px 10px; text-align: left;">Route</th>
                        <th style="border: 1px solid #000; padding: 3px 10px; text-align: left;">Dosages</th>
                        <th style="border: 1px solid #000; padding: 3px 10px; text-align: left;">Frequency</th>
                        <th style="border: 1px solid #000; padding: 3px 10px; text-align: left;">Duration</th>
                        <th style="border: 1px solid #000; padding: 3px 10px; text-align: left;">Notes</th>
                    </tr>
                </thead>
                <tbody>
                    ${groupMedicines.length > 0
                      ? groupMedicines.map((med, index) => `
                        <tr>
                            <td style="border: 1px solid #000; padding: 3px 10px;">${index + 1}</td>
                            <td style="border: 1px solid #000; padding: 3px 10px;">${med.medicine_name || med.medicineName || ''} - ${med.strength || ''} -${med.dose || ''}- ${med.packet_size || ''}</td>
                            <td style="border: 1px solid #000; padding: 3px 10px;">${med.substance || ''}</td>
                            <td style="border: 1px solid #000; padding: 3px 10px;">${med.route || ''}</td>
                            <td style="border: 1px solid #000; padding: 3px 10px;">${med.dosage || ''}</td>
                            <td style="border: 1px solid #000; padding: 3px 10px;">${med.frequency || ''}</td>
                            <td style="border: 1px solid #000; padding: 3px 10px;">${med.duration || ''}</td>
                            <td style="border: 1px solid #000; padding: 3px 10px;">${med.notes || ''}</td>
                        </tr>`).join('')
                      : `<tr><td colSpan="8">No Medicine Found</td></tr>`}
                </tbody>
            </table>
        </div>
        <div style="margin-top: 80px;">
            <p style="padding-bottom: 80px; margin-bottom: 20px; border-bottom: 1px solid #000; width: 230px;">
                <strong>Signature & Stamp:</strong>
            </p>
        </div>
      </div>`;

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(`
<html>
  <head>
    <title>${fileName}</title>
    <style>
      body { font-family: Arial, sans-serif; line-height: 1.2; background: #fff; }
      .main-wrapper { width: 210mm; margin: 0 auto; background: #fff; }
      @page { size: A4; margin: 5mm; }
      table { width: 100%; border-collapse: collapse; margin-top: 20px; }
      td, th { border: 1px solid #000; padding: 3px 10px; text-align: left; }
      .footer { position: fixed; bottom: 0mm; width: 100%; text-align: center; font-size: 12px; padding: 5px 0; }
    </style>
  </head>
  <body>
    <div class="main-wrapper">${content}</div>
    <div class="footer">
      <div style="margin-top: 190px; font-size: 12px; text-align: center; color: rgb(0,0,0);">
        Hospital Name: ${hospitalName} | Email:
        <span style="border-bottom: 1px solid rgb(44,100,223); color: rgb(44,100,223);">${email}</span>
        | Phone: ${phone} |
        <span style="border-bottom: 1px solid rgb(44,100,223); color: rgb(44,100,223);">Website: ${website}</span>
      </div>
    </div>
    <script>
      window.onload = function() { window.focus(); window.print(); };
    </script>
  </body>
</html>`);
    printWindow.document.close();
  };

  useEffect(() => {
    getdata();
    getRxList();
    getPrescriptions();
    fetchCategories();
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const res = await axiosInstance.get("getPharmacyAlerts");
      if (res.data?.success) setAlerts(res.data.data);
    } catch {}
  };

  const fetchCategories = async () => {
    try {
      const res = await axiosInstance.get(`getAllCategories`);
      if (res.data.success && Array.isArray(res.data.data)) setCategories(res.data.data);
    } catch (err) { console.error("Failed to fetch categories", err); }
  };

  const getdata = async () => {
    try {
      const response = await axiosInstance.get(`getDrugs`);
      if (response.data?.success) setDrugs(response.data.data);
    } catch (error) {}
  };

  const getRxList = async () => {
    try {
      const res = await axiosInstance.get(`getAllRxList`);
      if (res.data?.success) setRxList(res.data.data);
    } catch (err) { console.error("Error fetching Rx List:", err); }
  };

  const getPrescriptions = async () => {
    try {
      const response = await axiosInstance.get(`getAllPrescriptions`);
      if (response.data?.success) { setPrescriptions(response.data.data); return; }
    } catch (error) {
      console.error("Error fetching prescriptions:", error);
    }
    try {
      const altResponse = await axiosInstance.get(`getPrescriptions`);
      if (altResponse.data?.success) setPrescriptions(altResponse.data.data);
    } catch (altError) {
      console.error("Alternative prescription API also failed:", altError);
      setPrescriptions([]);
    }
  };

  const handlechange = (e) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
  };

  const apihitnav = async () => {
    if (activeTab === "tab2") {
      try {
        const formData = new FormData();
        const fields = ["name","substance","unit_of_measurement","company","quantity","category","strength","expiration_date","cost","price"];
        for (const key of fields) {
          if (!data[key]) { alert(`Please enter ${key}`); return; }
          formData.append(key, data[key]);
        }
        if (!editId && !imageFile) { alert("Please upload an image."); return; }
        if (imageFile) formData.append("image", imageFile);
        const apiURL = editId ? `updateDrug/${editId}` : `addDrug`;
        const method = editId ? "put" : "post";
        const response = await axiosInstance[method](apiURL, formData, { headers: { "Content-Type": "multipart/form-data" } });
        if (response.data?.success) {
          setModalOpen(false); setEditId(null); setData({}); setImageFile(null); getdata();
          Swal.fire("Success!", editId ? "Drug updated" : "Drug added", "success");
        }
      } catch (err) { alert("Error while saving drug."); }
    } else if (activeTab === "tab3") {
      await addRxMedicine();
    }
  };

  const hanldeAllergy = async () => {
    try {
      const response = await axios.get(`${baseurl}getAllAllergies`);
      if (response.data.success === true) setAllergies(response.data.data);
    } catch (error) { console.error("Error fetching allergy"); }
  };

  const addRxMedicine = async () => {
    try {
      const requiredFields = ["medicine_name","strength","unit","pharmaceutical_form","frequency","duration","notes","route","product_type","active_substances"];
      for (let field of requiredFields) {
        if (!data[field]) { alert(`Please enter ${field}`); return; }
      }
      const response = await axiosInstance.post(`addRxMedicine`, data);
      if (response.data?.success) {
        setModalOpen(false); setData({}); getRxList();
        Swal.fire("Success!", "Rx Medicine added", "success");
      } else {
        Swal.fire("Error", "Failed to add Rx Medicine", "error");
      }
    } catch (error) { alert("Error while adding Rx medicine."); }
  };

  const deleteRxMedicine = async (id) => {
    const result = await Swal.fire({ title: "Are you sure?", text: "You won't be able to revert this!", icon: "warning", showCancelButton: true, confirmButtonColor: "#3085d6", cancelButtonColor: "#d33", confirmButtonText: "Yes, delete it!" });
    if (result.isConfirmed) {
      try {
        const res = await axiosInstance.delete(`deleteRxMedicine/${id}`);
        if (res.data?.success) { getRxList(); Swal.fire("Deleted!", "Rx medicine has been deleted.", "success"); }
        else Swal.fire("Error!", "Failed to delete Rx medicine.", "error");
      } catch (err) { Swal.fire("Error!", "An error occurred while deleting Rx medicine.", "error"); }
    }
  };

  const deleteDrug = async (id) => {
    const result = await Swal.fire({ title: "Are you sure?", text: "You won't be able to revert this!", icon: "warning", showCancelButton: true, confirmButtonColor: "#3085d6", cancelButtonColor: "#d33", confirmButtonText: "Yes, delete it!" });
    if (result.isConfirmed) {
      try {
        const res = await axiosInstance.delete(`deleteDrug/${id}`);
        if (res.data?.success) { getdata(); Swal.fire("Deleted!", "Drug has been deleted.", "success"); }
        else Swal.fire("Error!", "Failed to delete drug.", "error");
      } catch (error) { Swal.fire("Error!", "An error occurred while deleting.", "error"); }
    }
  };

  const lowStockIds = new Set(alerts.low_stock.map(x => x.id));
  const filteredDrugs = drugs.filter(item => {
    if (showLowStockOnly) return lowStockIds.has(item.id);
    if (inventoryBranchFilter !== "all" && String(item.branch_id) !== inventoryBranchFilter) return false;
    return item.name?.toLowerCase().includes(searchDrug.toLowerCase()) ||
      item.barcode?.toLowerCase().includes(searchDrug.toLowerCase());
  });
  const filteredRxList = rxList.filter(item =>
    item.medicineName?.toLowerCase().includes(searchRx.toLowerCase())
  );
  const filteredPrescriptions = prescriptions.filter(item => {
    const term = searchPrescription.trim().toLowerCase();
    if (!term) return true;
    const patientName    = (item.fullName ?? item.patientName ?? item.patient_name ?? "").toString().toLowerCase();
    const prescriptionId = (item.prescriptionId ?? item.id ?? "").toString().toLowerCase();
    return patientName.includes(term) || prescriptionId.includes(term);
  });

  const totalPagesDrugs         = Math.ceil(filteredDrugs.length / itemsPerPage);
  const totalPagesRx            = Math.ceil(filteredRxList.length / itemsPerPage);
  const totalPagesPrescriptions = Math.ceil(filteredPrescriptions.length / itemsPerPage);

  const paginatedDrugs         = filteredDrugs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const paginatedRxList        = filteredRxList.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const paginatedPrescriptions = filteredPrescriptions.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const isExpired = (dateString) => {
    if (!dateString) return false;
    const exp = new Date(dateString); const today = new Date();
    exp.setHours(0,0,0,0); today.setHours(0,0,0,0);
    return exp < today;
  };

  const getCategoryName = (id) => {
    const cat = categories.find(c => String(c.id) === String(id));
    return cat ? cat.name : id;
  };

  const activeTabColor = activeTab === 'tab1' ? COLORS.blue : activeTab === 'tab2' ? COLORS.green : activeTab === 'tab4' ? COLORS2.purple : activeTab === 'tab5' ? COLORS2.teal : COLORS.orange;

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', overflow: 'hidden' }}>

          {/* ── Header ── */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: `${COLORS.blue}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="ti ti-pill" style={{ color: COLORS.blue, fontSize: 17 }} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Pharmacy</div>
              <div style={{ fontSize: 11, color: COLORS.gray }}>Manage prescriptions, inventory, vendors, batches, purchase orders and stock movements</div>
            </div>
          </div>

          {/* ── Tabs ── */}
          <div style={{ display: 'flex', gap: 4, padding: '10px 20px 0', borderBottom: '1px solid #e8ecf0', background: '#fafbfc' }}>
            {TABS.filter(tab => !tab.advancedInventory || advancedInventoryEnabled).map(tab => {
              const active   = activeTab === tab.id
              const disabled = !permissions[tab.permKey]
              const count    = tab.id === 'tab1' ? prescriptions.length : tab.id === 'tab2' ? drugs.length : tab.id === 'tab3' ? rxList.length : null
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    if (disabled) return
                    setActiveTab(tab.id); setCurrentPage(1)
                    if (tab.id === 'tab1') setSearchPrescription('')
                    else if (tab.id === 'tab2') { setSearchDrug(''); setShowLowStockOnly(false); }
                    else if (tab.id === 'tab3') setSearchRx('')
                  }}
                  disabled={disabled}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px',
                    fontSize: 13, fontWeight: active ? 700 : 500,
                    color: active ? tab.color : COLORS.gray,
                    background: active ? '#fff' : 'transparent',
                    border: '1px solid', borderColor: active ? '#e8ecf0' : 'transparent',
                    borderBottom: active ? `2px solid ${tab.color}` : '2px solid transparent',
                    borderRadius: '8px 8px 0 0', cursor: disabled ? 'not-allowed' : 'pointer',
                    marginBottom: -1, transition: 'all 0.15s', opacity: disabled ? 0.45 : 1,
                  }}
                >
                  <i className={`ti ${tab.icon}`} style={{ fontSize: 15 }} />
                  {tab.label}
                  {count !== null && (
                    <span style={{
                      fontSize: 10, fontWeight: 700, minWidth: 18, height: 18, borderRadius: 9,
                      background: active ? tab.color : '#e8ecf0',
                      color: active ? '#fff' : COLORS.gray,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px',
                    }}>
                      {count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          {/* ── Content ── */}
          {activeTab === null ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <div className="spinner-border" role="status" style={{ color: COLORS.blue }}>
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div style={{ padding: '14px 20px' }}>

              {/* Toolbar — only for original 3 tabs */}
              {['tab1','tab2','tab3'].includes(activeTab) && <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                <SearchBox
                  value={activeTab === 'tab1' ? searchPrescription : activeTab === 'tab2' ? searchDrug : searchRx}
                  onChange={e => {
                    if (activeTab === 'tab1') setSearchPrescription(e.target.value)
                    else if (activeTab === 'tab2') setSearchDrug(e.target.value)
                    else setSearchRx(e.target.value)
                    setCurrentPage(1)
                  }}
                  placeholder={
                    activeTab === 'tab1' ? 'Search by patient name or prescription ID...'
                    : activeTab === 'tab2' ? 'Search by medicine name or barcode...'
                    : 'Search by medicine name...'
                  }
                />
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: COLORS.gray }}>
                    {activeTab === 'tab1' ? filteredPrescriptions.length
                      : activeTab === 'tab2' ? filteredDrugs.length
                      : filteredRxList.length} record{(activeTab === 'tab1' ? filteredPrescriptions.length : activeTab === 'tab2' ? filteredDrugs.length : filteredRxList.length) !== 1 ? 's' : ''}
                  </span>
                  {activeTab === 'tab2' && (
                    <>
                      <button
                        className="bgBtn btn-sm d-flex align-items-center gap-1"
                        style={{ fontSize: 13, padding: '5px 12px' }}
                        onClick={() => navigate('/Admin/AddInventory')}
                      >
                        <i className="ti ti-plus" style={{ fontSize: 14 }} /> Add Inventory
                      </button>
                      <button
                        onClick={() => navigate('/Admin/Category')}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '5px 12px', borderRadius: 8, border: `1px solid ${COLORS.blue}`, background: 'transparent', color: COLORS.blue, cursor: 'pointer', fontWeight: 500 }}
                      >
                        <i className="ti ti-category" style={{ fontSize: 14 }} /> Categories
                      </button>
                      {allBranchSettings && allBranchSettings.length > 1 && (
                        <select
                          value={inventoryBranchFilter}
                          onChange={e => { setInventoryBranchFilter(e.target.value); setCurrentPage(1); }}
                          style={{
                            height: 34, borderRadius: 8, border: `1px solid ${inventoryBranchFilter !== 'all' ? COLORS.green : '#dee2e6'}`,
                            padding: '0 10px', fontSize: 13,
                            color: inventoryBranchFilter !== 'all' ? COLORS.green : COLORS.gray,
                            fontWeight: inventoryBranchFilter !== 'all' ? 600 : 400,
                            background: '#fff', cursor: 'pointer', outline: 'none', minWidth: 140,
                          }}
                        >
                          <option value="all">All Branches</option>
                          {allBranchSettings.map((b, i) => (
                            <option key={b.id} value={String(b.id)}>
                              Branch {i + 1}{b.official_name ? ` — ${b.official_name}` : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </>
                  )}
                  {activeTab === 'tab3' && (
                    <button
                      className="bgBtn btn-sm d-flex align-items-center gap-1"
                      style={{ fontSize: 13, padding: '5px 12px' }}
                      onClick={() => navigate('/Admin/AddRxList')}
                    >
                      <i className="ti ti-plus" style={{ fontSize: 14 }} /> Add Rx Medicine
                    </button>
                  )}
                </div>
              </div>}

              {/* ── Prescriptions Table ── */}
              {activeTab === 'tab1' && (
                <>
                  <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e8ecf0' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ ...thStyle, borderLeft: `3px solid ${COLORS.blue}` }}>ID</th>
                          <th style={thStyle}>Patient Name</th>
                          <th style={thStyle}>Doctor</th>
                          <th style={thStyle}>Date</th>
                          <th style={thStyle}>Medicine</th>
                          <th style={thStyle}>Strength</th>
                          <th style={thStyle}>Dose</th>
                          <th style={thStyle}>Frequency</th>
                          <th style={thStyle}>Duration</th>
                          <th style={thStyle}>Route</th>
                          <th style={thStyle}>Notes</th>
                          <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedPrescriptions.length === 0
                          ? <EmptyRow colSpan={12} message="No prescriptions found" />
                          : paginatedPrescriptions.map((item, index) => (
                            <tr
                              key={index}
                              style={{ background: index % 2 === 0 ? '#fff' : '#fafbfc' }}
                              onMouseEnter={e => e.currentTarget.style.background = `${COLORS.blue}0d`}
                              onMouseLeave={e => e.currentTarget.style.background = index % 2 === 0 ? '#fff' : '#fafbfc'}
                            >
                              <td style={tdStyle}>{item.id || item.prescriptionId}</td>
                              <td style={tdStyle}>
                                <NavLink
                                  to="/Admin/ManageAppointment"
                                  state={{ patientid: { id: item.patient_id || 'N/A' } }}
                                  style={{ color: '#2d3748', textDecoration: 'none', fontWeight: 500 }}
                                >
                                  {item.fullName || 'N/A'}
                                </NavLink>
                              </td>
                              <td style={tdStyle}>{item.doctorName || 'N/A'}</td>
                              <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                                {item.created_at ? new Date(item.created_at).toLocaleDateString('en-GB')
                                  : item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-GB') : 'N/A'}
                              </td>
                              <td style={{ ...tdStyle, ...truncateCell }} title={item.medicine_name || item.medicineName}>{item.medicine_name || item.medicineName || '—'}</td>
                              <td style={tdStyle}>{item.strength || '—'}</td>
                              <td style={tdStyle}>{item.dose || '—'}</td>
                              <td style={{ ...tdStyle, ...truncateCell }} title={item.frequency}>{item.frequency || '—'}</td>
                              <td style={tdStyle}>{item.duration || '—'}</td>
                              <td style={tdStyle}>{item.route || '—'}</td>
                              <td style={{ ...tdStyle, ...truncateCell }} title={item.notes}>{item.notes || '—'}</td>
                              <td style={{ ...tdStyle, whiteSpace: 'nowrap', textAlign: 'center' }}>
                                <NavLink
                                  to={`/Admin/ViewPrescription/${item.patient_id}`}
                                  title="View"
                                  style={actionIcon(COLORS.blue)}
                                >
                                  <i className="ti ti-eye" />
                                </NavLink>
                                <button
                                  onClick={() => handlePrintRow(item)}
                                  title="Print"
                                  style={actionIcon(COLORS.gray)}
                                >
                                  <i className="ti ti-printer" />
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                    <Pagination currentPage={currentPage} totalPages={totalPagesPrescriptions} setCurrentPage={setCurrentPage} />
                  </div>
                </>
              )}

              {/* ── Inventory Table ── */}
              {activeTab === 'tab2' && (
                <>
                  {/* Alerts Banner */}
                  {(alerts.low_stock.length > 0 || alerts.near_expiry.length > 0 || alerts.expired.length > 0) && (
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                      {alerts.low_stock.length > 0 && (
                        <div
                          onClick={() => { setShowLowStockOnly(s => !s); setSearchDrug(''); setCurrentPage(1); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer', userSelect: 'none', transition: 'all 0.15s',
                            background: showLowStockOnly ? '#ffc107' : '#fff3cd',
                            border: `1px solid ${showLowStockOnly ? '#856404' : '#ffc107'}`,
                          }}
                          title="Click to filter inventory to low-stock items"
                        >
                          <i className="ti ti-alert-triangle" style={{ color: '#856404', fontSize: 16 }} />
                          <span style={{ color: '#856404', fontWeight: 600 }}>{alerts.low_stock.length} item{alerts.low_stock.length !== 1 ? 's' : ''} low on stock</span>
                          {showLowStockOnly
                            ? <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 6, background: '#856404', color: '#fff', marginLeft: 2 }}>Filtered ✕</span>
                            : <i className="ti ti-filter" style={{ color: '#856404', fontSize: 12, marginLeft: 2 }} />}
                        </div>
                      )}
                      {alerts.near_expiry.length > 0 && (
                        <div
                          onClick={() => { setActiveTab('tab5'); setCurrentPage(1); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 8, background: '#fff3cd', border: '1px solid #fd7e14', fontSize: 13, cursor: 'pointer', userSelect: 'none' }}
                          title="Click to view expiring batches"
                        >
                          <i className="ti ti-clock-exclamation" style={{ color: '#984c0c', fontSize: 16 }} />
                          <span style={{ color: '#984c0c', fontWeight: 600 }}>{alerts.near_expiry.length} batch{alerts.near_expiry.length !== 1 ? 'es' : ''} expiring within 30 days</span>
                          <i className="ti ti-arrow-right" style={{ color: '#984c0c', fontSize: 12 }} />
                        </div>
                      )}
                      {alerts.expired.length > 0 && (
                        <div
                          onClick={() => { setActiveTab('tab5'); setCurrentPage(1); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 8, background: '#f8d7da', border: '1px solid #dc3545', fontSize: 13, cursor: 'pointer', userSelect: 'none' }}
                          title="Click to view expired batches"
                        >
                          <i className="ti ti-ban" style={{ color: '#842029', fontSize: 16 }} />
                          <span style={{ color: '#842029', fontWeight: 600 }}>{alerts.expired.length} expired batch{alerts.expired.length !== 1 ? 'es' : ''} still in stock</span>
                          <i className="ti ti-arrow-right" style={{ color: '#842029', fontSize: 12 }} />
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e8ecf0' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ ...thStyle, borderLeft: `3px solid ${COLORS.green}` }}>SN</th>
                          <th style={thStyle}>Photo</th>
                          <th style={thStyle}>ID</th>
                          <th style={thStyle}>Category</th>
                          <th style={thStyle}>Name</th>
                          <th style={thStyle}>Substance</th>
                          <th style={thStyle}>Strength</th>
                          <th style={thStyle}>Unit</th>
                          <th style={thStyle}>Company</th>
                          <th style={thStyle}>Qty</th>
                          <th style={thStyle}>Expiry</th>
                          <th style={thStyle}>Cost</th>
                          <th style={thStyle}>Price</th>
                          <th style={thStyle}>Vat%</th>
                          <th style={thStyle}>Vat</th>
                          <th style={thStyle}>Total</th>
                          <th style={thStyle}>Control</th>
                          <th style={{ ...thStyle, minWidth: 200 }}>Barcode</th>
                          <th style={thStyle}>Branch</th>
                          <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedDrugs.length === 0
                          ? <EmptyRow colSpan={20} message="No inventory items found" />
                          : paginatedDrugs.map((item, index) => (
                            <tr
                              key={index}
                              style={{ background: index % 2 === 0 ? '#fff' : '#fafbfc' }}
                              onMouseEnter={e => e.currentTarget.style.background = `${COLORS.green}0d`}
                              onMouseLeave={e => e.currentTarget.style.background = index % 2 === 0 ? '#fff' : '#fafbfc'}
                            >
                              <td style={tdStyle}>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                              <td style={tdStyle}>
                                <img
                                  src={item.image ? `${baseurImage}${item.image}` : '../assets/images/user/avatar-1.jpg'}
                                  alt="Drug"
                                  onError={e => { e.currentTarget.src = '../assets/images/user/avatar-1.jpg' }}
                                  style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'cover', border: `1.5px solid ${COLORS.green}44` }}
                                />
                              </td>
                              <td style={tdStyle}>{item.id}</td>
                              <td style={tdStyle}>
                                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: `${COLORS.blue}18`, color: COLORS.blue, whiteSpace: 'nowrap' }}>
                                  {getCategoryName(item.category)}
                                </span>
                              </td>
                              <td style={{ ...tdStyle, fontWeight: 500 }}>{item.name}</td>
                              <td style={{ ...tdStyle, ...truncateCell }} title={item.substance}>{item.substance}</td>
                              <td style={tdStyle}>{item.strength}</td>
                              <td style={tdStyle}>{item.unit_of_measurement}</td>
                              <td style={tdStyle}>{item.company}</td>
                              <td style={tdStyle}>
                                {(() => {
                                  const qty = Number(item.quantity);
                                  const reorder = Number(item.reorder_level ?? 10);
                                  if (qty === 0) return <span style={{ color: COLORS.red, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: `${COLORS.red}15` }}>{qty}</span>;
                                  if (qty <= reorder) return <span style={{ color: COLORS.orange, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: `${COLORS.orange}15` }}>{qty}</span>;
                                  return <span style={{ color: COLORS.green, fontWeight: 600 }}>{qty}</span>;
                                })()}
                              </td>
                              <td style={tdStyle}>
                                {isExpired(item.expiration_date)
                                  ? <span style={{ color: COLORS.red, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: `${COLORS.red}15`, whiteSpace: 'nowrap' }}>
                                      {new Date(item.expiration_date).toLocaleDateString('en-GB')}
                                    </span>
                                  : <span style={{ whiteSpace: 'nowrap' }}>{new Date(item.expiration_date).toLocaleDateString('en-GB')}</span>}
                              </td>
                              <td style={tdStyle}>{item.cost}</td>
                              <td style={tdStyle}>{item.price}</td>
                              <td style={tdStyle}>{item.vat_value}%</td>
                              <td style={tdStyle}>{item.vat_amount}</td>
                              <td style={tdStyle}>{item.after_vat}</td>
                              <td style={tdStyle}>
                                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: item.control === 1 ? `${COLORS.orange}18` : '#f0f2f5', color: item.control === 1 ? COLORS.orange : COLORS.gray }}>
                                  {item.control === 1 ? 'Yes' : 'No'}
                                </span>
                              </td>
                              <td style={{ ...tdStyle, minWidth: 200 }}>
                                {item.barcode && (
                                  <div style={{ maxWidth: 150 }}>
                                    <BarcodeGenerator value={String(item.barcode)} width={2} height={50} />
                                    <div style={{ textAlign: 'center', fontFamily: 'monospace', fontSize: 10, color: COLORS.gray, marginTop: 2 }}>
                                      {item.barcode}
                                    </div>
                                  </div>
                                )}
                              </td>
                              <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                                {(() => {
                                  const bid = item.branch_id;
                                  if (!bid || !allBranchSettings || allBranchSettings.length === 0) return <span style={{ color: COLORS.gray, fontSize: 11 }}>—</span>;
                                  const idx = allBranchSettings.findIndex(b => b.id === bid);
                                  const b = allBranchSettings[idx];
                                  return (
                                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: `${COLORS.purple}15`, color: COLORS.purple, whiteSpace: 'nowrap' }}>
                                      {b?.official_name || `Branch ${idx + 1}`}
                                    </span>
                                  );
                                })()}
                              </td>
                              <td style={{ ...tdStyle, whiteSpace: 'nowrap', textAlign: 'center' }}>
                                <NavLink to={`/Admin/ViewInventory/${item.id}`} title="View" style={actionIcon(COLORS.blue)}>
                                  <i className="ti ti-eye" />
                                </NavLink>
                                <button
                                  onClick={() => navigate('/Admin/EditInventory', { state: { drug: item } })}
                                  title="Edit"
                                  style={actionIcon(COLORS.orange)}
                                >
                                  <i className="ti ti-edit" />
                                </button>
                                <button
                                  onClick={() => deleteDrug(item.id)}
                                  title="Delete"
                                  style={actionIcon(COLORS.red)}
                                >
                                  <i className="ti ti-trash" />
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                    <Pagination currentPage={currentPage} totalPages={totalPagesDrugs} setCurrentPage={setCurrentPage} />
                  </div>
                </>
              )}

              {/* ── Rx Standard List Table ── */}
              {activeTab === 'tab3' && (
                <>
                  <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e8ecf0' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ ...thStyle, borderLeft: `3px solid ${COLORS.orange}` }}>ID</th>
                          <th style={thStyle}>Medicine</th>
                          <th style={thStyle}>Strength</th>
                          <th style={thStyle}>Unit</th>
                          <th style={thStyle}>Form</th>
                          <th style={thStyle}>Route</th>
                          <th style={thStyle}>Substance</th>
                          <th style={thStyle}>Type</th>
                          <th style={thStyle}>Pack Size</th>
                          <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedRxList.length === 0
                          ? <EmptyRow colSpan={10} message="No Rx medicines found" />
                          : paginatedRxList.map((item, index) => (
                            <tr
                              key={index}
                              style={{ background: index % 2 === 0 ? '#fff' : '#fafbfc' }}
                              onMouseEnter={e => e.currentTarget.style.background = `${COLORS.orange}0d`}
                              onMouseLeave={e => e.currentTarget.style.background = index % 2 === 0 ? '#fff' : '#fafbfc'}
                            >
                              <td style={tdStyle}>{item.rxId}</td>
                              <td style={{ ...tdStyle, fontWeight: 500 }}>{item.medicineName}</td>
                              <td style={tdStyle}>{item.strength}</td>
                              <td style={tdStyle}>{item.unit}</td>
                              <td style={{ ...tdStyle, ...truncateCell }} title={item.form}>{item.form}</td>
                              <td style={tdStyle}>{item.route}</td>
                              <td style={{ ...tdStyle, ...truncateCell }} title={item.activeSubstances}>{item.activeSubstances}</td>
                              <td style={tdStyle}>
                                {item.productType && (
                                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: `${COLORS.orange}18`, color: COLORS.orange, whiteSpace: 'nowrap' }}>
                                    {item.productType}
                                  </span>
                                )}
                              </td>
                              <td style={tdStyle}>{item.packet_size || '—'}</td>
                              <td style={{ ...tdStyle, whiteSpace: 'nowrap', textAlign: 'center' }}>
                                <NavLink to={`/Admin/ViewRx/${item.rxId}`} title="View" style={actionIcon(COLORS.blue)}>
                                  <i className="ti ti-eye" />
                                </NavLink>
                                <button
                                  onClick={() => navigate('/Admin/EditRxList', { state: { Rx: item } })}
                                  title="Edit"
                                  style={actionIcon(COLORS.orange)}
                                >
                                  <i className="ti ti-edit" />
                                </button>
                                <button
                                  onClick={() => deleteRxMedicine(item.rxId)}
                                  title="Delete"
                                  style={actionIcon(COLORS.red)}
                                >
                                  <i className="ti ti-trash" />
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                    <Pagination currentPage={currentPage} totalPages={totalPagesRx} setCurrentPage={setCurrentPage} />
                  </div>
                </>
              )}

              {/* ── Vendors Tab ── */}
              {activeTab === 'tab4' && <Vendors />}

              {/* ── Batches Tab ── */}
              {activeTab === 'tab5' && <Batches />}

              {/* ── Purchase Orders Tab ── */}
              {activeTab === 'tab6' && <PurchaseOrders />}

              {/* ── Stock Movements Tab ── */}
              {activeTab === 'tab7' && <StockMovements />}

              {/* ── Analytics Tab ── */}
              {activeTab === 'tab8' && <Analytics />}

            </div>
          )}

        </div>

        {/* ── View Modal ── */}
        {viewModalOpen && viewItem && (
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setViewModalOpen(false)}
          >
            <div
              style={{ background: '#fff', borderRadius: 12, width: 420, padding: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
              onClick={e => e.stopPropagation()}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                <div style={{ width: 34, height: 34, borderRadius: 8, background: `${COLORS.blue}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="ti ti-eye" style={{ color: COLORS.blue, fontSize: 18 }} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>
                    {activeTab === 'tab2' ? 'Drug Details' : 'Rx Medicine Details'}
                  </div>
                </div>
                <button
                  onClick={() => setViewModalOpen(false)}
                  style={{ marginLeft: 'auto', width: 26, height: 26, borderRadius: 6, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.gray, fontSize: 14 }}
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <div style={{ borderTop: '1px solid #f0f2f5', paddingTop: 14 }}>
                {activeTab === 'tab2' ? (
                  <>
                    {[['Name', viewItem.name], ['Substance', viewItem.substance], ['Company', viewItem.company], ['Strength', viewItem.strength], ['Cost', viewItem.cost], ['Price', viewItem.price], ['Expiry', new Date(viewItem.expiration_date).toLocaleDateString('en-GB')]].map(([label, val]) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f5f7fa', fontSize: 13 }}>
                        <span style={{ color: COLORS.gray, fontWeight: 600 }}>{label}</span>
                        <span style={{ color: '#2d3748' }}>{val}</span>
                      </div>
                    ))}
                    <img src={`${baseurImage}${viewItem.image}`} alt="drug" style={{ width: '100%', borderRadius: 8, marginTop: 14 }} />
                  </>
                ) : (
                  <>
                    {[['Medicine Name', viewItem.medicineName], ['Strength', viewItem.strength], ['Unit', viewItem.unit], ['Pharmaceutical Form', viewItem.form], ['Frequency', viewItem.frequency], ['Duration', viewItem.duration], ['Notes', viewItem.notes], ['Route', viewItem.route], ['Product Type', viewItem.productType], ['Active Substances', viewItem.activeSubstances]].map(([label, val]) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f5f7fa', fontSize: 13 }}>
                        <span style={{ color: COLORS.gray, fontWeight: 600 }}>{label}</span>
                        <span style={{ color: '#2d3748', textAlign: 'right', maxWidth: '60%' }}>{val}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Add / Edit Modal ── */}
        {modalOpen && (
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setModalOpen(false)}
          >
            <div
              style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 720, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div style={{ padding: '16px 24px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: `${COLORS.blue}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="ti ti-plus" style={{ color: COLORS.blue, fontSize: 16 }} />
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>
                  {editId ? 'Edit Drug' : 'Add Item'}
                </div>
                <button
                  onClick={() => setModalOpen(false)}
                  style={{ marginLeft: 'auto', width: 26, height: 26, borderRadius: 6, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.gray, fontSize: 14 }}
                >
                  <i className="ti ti-x" />
                </button>
              </div>

              {/* Modal Body */}
              <div style={{ padding: '20px 24px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  {activeTab === 'tab2' ? (
                    <>
                      {["name","substance","unit_of_measurement","company","quantity","category","strength","expiration_date","cost","price"].map((field, idx) => (
                        <div key={idx} style={{ flex: '1 1 200px' }}>
                          <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {field.replace(/_/g, ' ')}
                          </label>
                          <input
                            type={field === 'expiration_date' ? 'date' : 'text'}
                            name={field}
                            value={data[field] || ''}
                            onChange={handlechange}
                            style={{ width: '100%', height: 36, border: '1.5px solid #dde3eb', borderRadius: 8, padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none', boxSizing: 'border-box' }}
                            onFocus={e => e.target.style.borderColor = COLORS.blue}
                            onBlur={e => e.target.style.borderColor = '#dde3eb'}
                          />
                        </div>
                      ))}
                      <div style={{ flex: '1 1 200px' }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Image</label>
                        <input
                          type="file"
                          name="image"
                          onChange={e => setImageFile(e.target.files[0])}
                          style={{ width: '100%', height: 36, border: '1.5px solid #dde3eb', borderRadius: 8, padding: '4px 10px', fontSize: 13, color: '#1a202c', outline: 'none', boxSizing: 'border-box' }}
                        />
                      </div>
                    </>
                  ) : (
                    ["medicine_name","strength","unit","pharmaceutical_form","frequency","duration","notes","route","product_type","active_substances"].map((field, idx) => (
                      <div key={idx} style={{ flex: '1 1 260px' }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {field.replace(/_/g, ' ')}
                        </label>
                        <input
                          type="text"
                          name={field}
                          value={data[field] || ''}
                          onChange={handlechange}
                          style={{ width: '100%', height: 36, border: '1.5px solid #dde3eb', borderRadius: 8, padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none', boxSizing: 'border-box' }}
                          onFocus={e => e.target.style.borderColor = COLORS.blue}
                          onBlur={e => e.target.style.borderColor = '#dde3eb'}
                        />
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div style={{ padding: '14px 24px', borderTop: '1px solid #e8ecf0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button
                  onClick={() => setModalOpen(false)}
                  style={{ padding: '7px 16px', fontSize: 13, borderRadius: 8, border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button onClick={apihitnav} className="bgBtn btn-sm d-flex align-items-center gap-1" style={{ padding: '7px 16px', fontSize: 13 }}>
                  {editId ? 'Update Drug' : 'Add Drug'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div ref={pdfRef} style={{ display: 'none' }} />
      </div>
    </div>
  )
}
